import { Component } from '@theme/component';
import { eventBus } from '@theme/events';
import { CartActions, CartRemoveEvent } from '@theme/cart--state--actions';
import { createCartSectionHandler } from '@theme/cart-section';
import { morphChildren, parseSection } from '@theme/morph';
import '@theme/shared--country-modal-trigger';

const { drawerOpened, previewOpened } = CartActions;

/**
 * Cart Drawer Web Component
 *
 * Listens to UI command events (cart:preview-opened, cart:drawer-opened) to open,
 * and result events (cart:item-added, cart:updated) to render content.
 *
 * Architecture (ngrx-style):
 * - UI Commands: cart:preview-opened (opens with optimistic add), cart:drawer-opened (just opens)
 * - Results: cart:item-added (morph real content), cart:add-failed (revert optimistic)
 *
 * Supports optimistic add: on cart:preview-opened, if the variant already exists
 * in the cart, the existing item is moved to the top with a highlight pulse.
 * After morph replaces content with server truth, the item is moved to top again
 * (Shopify maintains insertion order, so morph would restore the original position).
 * If it's a new variant, a preview element is prepended and replaced by morph.
 *
 * Uses inline section HTML from the event when available to avoid a
 * redundant fetch; otherwise delegates to the cart store.
 *
 * @attr {string} data-section-id - The section ID for Section Rendering API requests
 * @attr {string} data-content-selector - CSS selector for the content container (default: '[data-cart-content]')
 *
 * @example
 * <overlay-component>
 *   <overlay-panel>
 *     <cart-drawer data-section-id="{{ section.id }}">
 *       <div data-cart-content>
 *         <ul class="cart-drawer__items">
 *           <!-- cart-item blocks rendered per line item -->
 *         </ul>
 *       </div>
 *     </cart-drawer>
 *   </overlay-panel>
 * </overlay-component>
 *
 * @extends {Component}
 */
class CartDrawer extends Component {
	/** @type {(() => void)[]} */
	#unsubscribes = [];

	/** @type {import('./cart--section.js').CartSectionHandler|null} */
	#sectionHandler = null;

	/**
	 * Optimistic state for revert on error.
	 * @type {{ type: 'existing', element: HTMLElement } | { type: 'new', element: HTMLElement } | null}
	 */
	#optimisticState = null;

	/**
	 * Variant ID to move to top after the next morph.
	 * Set by optimistic Path A, consumed by #morphContent, cleared by #revertOptimistic.
	 * @type {string|null}
	 */
	#pendingMoveToTop = null;

	/** @type {import('./shared--overlay.js').OverlayComponent|null} */
	#overlay = null;

	/** @type {ResizeObserver|null} */
	#scrollableObserver = null;

	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {Map<string, HTMLElement>} Track pending removals for fallback */
	#pendingRemovals = new Map();

	/**
	 * CSS selector for finding the content container.
	 * Configurable via data-content-selector attribute.
	 * @returns {string}
	 */
	get #contentSelector() {
		return this.dataset.contentSelector || '[data-cart-content]';
	}

	/**
	 * The content container element.
	 * @returns {HTMLElement|null}
	 */
	get #contentEl() {
		return this.querySelector(this.#contentSelector);
	}

	/**
	 * The parent overlay component (cached on connect).
	 * @returns {import('./shared--overlay.js').OverlayComponent|null}
	 */
	get #drawer() {
		return this.#overlay;
	}

	connectedCallback() {
		super.connectedCallback();

		this.#overlay = this.closest('overlay-component');
		if (!this.#overlay) {
			console.warn('CartDrawer: no parent overlay-component found');
		}

		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		// Remove-link clicks — emit action event instead of following the plain
		// href (which would navigate to /cart/change, redirecting away from the
		// drawer). Same pattern as cart--page.js's remove handling.
		//
		// Guarded against duplicate clicks for the same key: some third-party
		// integrations (e.g. the wishlist app's "move to wishlist" flow)
		// programmatically .click() this link twice in a row "for safety". The
		// item hasn't left the DOM yet at that point (removal only happens on
		// the fade's transitionend), so a second click would emit a second
		// CartRemoveEvent for a line the first request may have already
		// removed server-side — that second request fails, and the
		// updateFailed fallback below then navigates to a now-stale remove
		// URL, surfacing Shopify's native "no valid id or line parameter"
		// error page.
		this.addEventListener('click', (event) => {
			const link = event.target.closest('[data-remove-key]');
			if (!link) return;

			event.preventDefault();
			const key = link.dataset.removeKey;
			if (this.#pendingRemovals.has(key)) return;

			const item = link.closest('[data-key]');

			if (item) {
				this.#pendingRemovals.set(key, item);
			}

			eventBus.emit(new CartRemoveEvent({
				data: { key },
			}));

			if (item) {
				item.style.opacity = '0';
				item.addEventListener('transitionend', () => item.remove(), { once: true });
			}
		}, { signal });

		// Restore the item and fall back to native navigation if the AJAX
		// removal fails.
		this.#unsubscribes.push(
			eventBus.on(CartActions.updateFailed, (event) => {
				const { key } = event.detail.data;
				const item = this.#pendingRemovals.get(key);
				if (item) {
					item.style.opacity = '1';
					this.#pendingRemovals.delete(key);
					const link = item.querySelector('[data-remove-key]');
					if (link?.href) {
						window.location.href = link.href;
					}
				}
			})
		);

		this.#unsubscribes.push(
			eventBus.on(CartActions.updated, () => {
				this.#pendingRemovals.clear();
			})
		);

		// ─────────────────────────────────────────────────────────────────────────
		// Cart section handler — registration, event listening, store subscription
		// ─────────────────────────────────────────────────────────────────────────

		if (this.dataset.sectionId) {
			this.#sectionHandler = createCartSectionHandler({
				sectionId: this.dataset.sectionId,
				onRender: (html) => {
					this.#optimisticState = null;
					this.#morphContent(html);
				},
				onAdd: (event) => {
					if (!event.detail?.data?.sections?.[this.dataset.sectionId]) {
						this.#optimisticState = null;
						this.#sectionHandler?.fetchAndRender();
					}
				},
				onError: () => {
					this.#revertOptimistic();
					this.#sectionHandler?.fetchAndRender();
				},
			});
		}

		// ─────────────────────────────────────────────────────────────────────────
		// UI Commands - when someone wants drawer behavior
		// ─────────────────────────────────────────────────────────────────────────

		// Open drawer on explicit request (no preview) — not a result of adding
		// an item, so the "item added" confirmation message shouldn't show.
		this.#unsubscribes.push(
			eventBus.on(drawerOpened, () => {
				delete this.dataset.justAdded;
				this.#drawer?.open();
			})
		);

		// Preview: apply optimistic add and open — this IS a result of adding
		// an item, so reveal the "item added" confirmation message.
		this.#unsubscribes.push(
			eventBus.on(previewOpened, (event) => {
				const { preview, variantId } = event.detail?.data ?? {};
				if (preview) {
					this.dataset.justAdded = 'true';
					this.#applyOptimisticAdd(preview, variantId);
					this.#drawer?.open();
				}
			})
		);

		// Note: quantity:remove is bound via on:quantity:remove="/handleQuantityRemove" in HTML

		// ─────────────────────────────────────────────────────────────────────────
		// Mobile: only split subtotal/shipping out of the footer once the item
		// list actually needs to scroll (see [data-scrollable] in cart--content.liquid)
		// ─────────────────────────────────────────────────────────────────────────

		// Observes .cart-drawer__content, NOT .cart-drawer__items — the latter's
		// box size is exactly what [data-scrollable] changes, so watching it would
		// feed back into itself (toggle → resize fires → re-measure → toggle again).
		// .cart-drawer__content's height comes from the outer flex chain (fixed to
		// the drawer viewport) and isn't affected by our own toggle, so it only
		// fires on genuine external resizes (viewport/orientation/dvh changes).
		if (this.#contentEl) {
			this.#scrollableObserver = new ResizeObserver(() => this.#updateScrollableState());
			this.#scrollableObserver.observe(this.#contentEl);
		}
		this.#updateScrollableState();
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		this.#sectionHandler?.cleanup();
		this.#sectionHandler = null;
		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
		this.#optimisticState = null;
		this.#pendingMoveToTop = null;
		this.#overlay = null;
		this.#abortController?.abort();
		this.#abortController = null;
		this.#pendingRemovals.clear();
		this.#scrollableObserver?.disconnect();
		this.#scrollableObserver = null;
	}

	/**
	 * Toggles [data-scrollable] based on whether the item list would overflow
	 * its available flex space. Always clears the attribute first to force
	 * the baseline (non-split) layout before measuring — reading
	 * scrollHeight/clientHeight while already in split mode would otherwise
	 * always report "no overflow" (that mode removes the very clipping being
	 * measured), so a shrinking cart could never drop back out of it.
	 */
	#updateScrollableState() {
		delete this.dataset.scrollable;

		const list = this.#contentEl?.querySelector('.cart-drawer__items');
		if (!list) return;

		if (list.scrollHeight > list.clientHeight + 1) {
			this.dataset.scrollable = 'true';
		}
	}

	// -- Declarative event handlers --

	/**
	 * Handle quantity:remove event — bound via on:quantity:remove="/handleQuantityRemove"
	 * Provides immediate visual feedback when quantity-selector removes an item (qty → 0)
	 * @param {CustomEvent} event
	 */
	handleQuantityRemove(event) {
		const item = event.target.closest('.cart-drawer__item');
		if (item) {
			item.style.opacity = '0';
			item.addEventListener('transitionend', () => item.remove(), { once: true });
		}
	}

	// -- Optimistic add system --

	/**
	 * Apply optimistic UI for an add-to-cart action.
	 *
	 * Path A: If the variant already exists in the cart (matched via data-key),
	 * moves it to the top of the list and adds a highlight pulse. Does NOT touch
	 * quantity — morph handles the real update. After morph restores server order,
	 * #morphContent moves the item to top again (via #pendingMoveToTop).
	 *
	 * Path B: If it's a new variant, creates a preview element with product data
	 * and prepends it to the list. Morph replaces it with the real item.
	 *
	 * @param {{ title: string, price: string, imageSrc: string, imageRatio?: number, productUrl: string, variantTitle: string, color?: string }} preview
	 * @param {string} [variantId]
	 */
	#applyOptimisticAdd(preview, variantId) {
		this.#revertOptimistic();

		const contentEl = this.#contentEl;
		if (!contentEl) return;

		const list = contentEl.querySelector('.cart-drawer__items');

		// Path A: variant already in cart — move to top, highlight, let morph update data
		if (variantId && list) {
			const existing = list.querySelector(`[data-key^="${variantId}:"]`);
			if (existing) {
				list.prepend(existing);
				existing.classList.add('cart-drawer__item--optimistic');
				this.#pendingMoveToTop = variantId;
				this.#optimisticState = { type: 'existing', element: existing };
				return;
			}
		}

		const li = document.createElement('li');
		li.className = 'cart-drawer__item cart-item cart-drawer__item--optimistic';
		if (variantId) li.setAttribute('data-key', `${variantId}:optimistic`);

		if (preview.imageSrc) {
			const imageWidth = parseInt(list?.querySelector('img[data-cart-child="image"]')?.getAttribute('width') ?? '', 10) || 93;
			const imageHeight = preview.imageRatio ? Math.round(imageWidth / preview.imageRatio) : imageWidth;
			const link = document.createElement('a');
			link.className = 'cart-item__image-link';
			link.href = preview.productUrl;
			const img = document.createElement('img');
			img.setAttribute('data-cart-child', 'image');
			img.setAttribute('data-skip-node-update', '');
			img.src = preview.imageSrc;
			img.alt = preview.title;
			img.width = imageWidth;
			img.height = imageHeight;
			img.loading = 'eager';
			link.appendChild(img);
			li.appendChild(link);
		}

		const details = document.createElement('div');
		details.className = 'cart-drawer__item-details';
		details.setAttribute('data-cart-child', 'info');

		if (preview.title) {
			const link = document.createElement('a');
			link.setAttribute('data-cart-child', 'title');
			link.href = preview.productUrl;
			link.textContent = preview.title;
			details.appendChild(link);
		}

		if (preview.color) {
			const color = document.createElement('p');
			color.setAttribute('data-cart-child', 'color');
			const colorLabel = this.dataset.colorLabel;
			color.textContent = colorLabel ? `${colorLabel} ${preview.color}` : preview.color;
			details.appendChild(color);
		}

		if (preview.variantTitle) {
			const variant = document.createElement('p');
			variant.setAttribute('data-cart-child', 'variant');
			const sizeLabel = this.dataset.sizeLabel;
			variant.textContent = sizeLabel ? `${sizeLabel} ${preview.variantTitle}` : preview.variantTitle;
			details.appendChild(variant);
		}

		if (preview.price) {
			const price = document.createElement('p');
			price.className = 'cart-drawer__item-price';
			const current = document.createElement('span');
			current.className = 'cart-drawer__item-current-price';
			current.textContent = preview.price;
			price.appendChild(current);
			details.appendChild(price);
		}

		const qty = document.createElement('quantity-selector');
		qty.setAttribute('data-state', 'loading');
		qty.innerHTML = `
			<button type="button" class="quantity-selector__button" disabled>&minus;</button>
			<input type="number" class="quantity-selector__input" value="1" disabled aria-label="Quantity">
			<button type="button" class="quantity-selector__button" disabled>+</button>`;
		details.appendChild(qty);

		const stock = document.createElement('p');
		stock.className = 'cart-item-stock cart-item-stock--placeholder';
		stock.setAttribute('data-cart-child', 'stock');
		stock.setAttribute('aria-hidden', 'true');
		stock.textContent = '\u00a0';
		details.appendChild(stock);

		li.appendChild(details);

		if (!list) {
			contentEl.innerHTML = '';
			const newList = document.createElement('ul');
			newList.className = 'cart-drawer__items';
			newList.setAttribute('role', 'list');
			contentEl.appendChild(newList);
			newList.prepend(li);
		} else {
			list.prepend(li);
		}

		this.#optimisticState = { type: 'new', element: li };
		this.#updateScrollableState();
	}

	/**
	 * Revert optimistic UI changes. Called on error to restore previous state.
	 * Path A (existing): remove highlight class (fetchAndRender morph restores position).
	 * Path B (new): remove the preview element.
	 */
	#revertOptimistic() {
		if (!this.#optimisticState) return;
		const { type, element } = this.#optimisticState;

		if (type === 'new') {
			element.remove();
		} else if (type === 'existing') {
			element.classList.remove('cart-drawer__item--optimistic');
		}

		this.#optimisticState = null;
		this.#pendingMoveToTop = null;
	}

	/**
	 * Resize a Shopify CDN image URL to a given width.
	 * Transforms e.g. `_800x.jpg` → `_150x.jpg` or appends width param.
	 * Falls through to the original URL if the pattern doesn't match.
	 * @param {string} url
	 * @param {number} width
	 * @returns {string}
	 */
	#resizeShopifyUrl(url, width) {
		const resized = url.replace(/_\d+x\./, `_${width}x.`);
		if (resized !== url) return resized;

		try {
			const parsed = new URL(url);
			parsed.searchParams.set('width', String(width));
			return parsed.toString();
		} catch {
			return url;
		}
	}

	/**
	 * Morph cart drawer content by diffing old vs new items by `data-key`.
	 * Items that haven't changed stay untouched (no repaint). New items get a
	 * fade-in animation. Removed items (including optimistic preview nodes with
	 * non-matching keys) are deleted. Footer is always updated.
	 *
	 * After morph, if #pendingMoveToTop is set, the matching item is moved to
	 * the top of the list. This keeps the recently-added item visible even though
	 * Shopify's cart maintains insertion order (morph would restore original position).
	 *
	 * @param {string} html - Full section HTML from Section Rendering API
	 */
	#morphContent(html) {
		const contentEl = this.#contentEl;
		if (!contentEl) return;

		const newContentEl = parseSection(html, this.#contentSelector);
		if (!newContentEl) return;

		try {
			const oldList = contentEl.querySelector('.cart-drawer__items');
			const newList = newContentEl.querySelector('.cart-drawer__items');

			// Transition between empty ↔ non-empty: full swap
			if (!oldList || !newList) {
				contentEl.innerHTML = newContentEl.innerHTML;
				this.#applyPendingMoveToTop(contentEl);
				this.#updateScrollableState();
				return;
			}

			// Remove optimistic items before morph — their keys don't match server keys,
			// so morph would treat the server item as "new" and replay entry animation.
			// Preserve the optimistic image to avoid a re-render flash.
			const optimistic = oldList.querySelector('.cart-drawer__item--optimistic');
			const skipEntryAnimation = !!optimistic;
			const cachedImg = optimistic?.querySelector('img[data-cart-child="image"]');
			if (optimistic) optimistic.remove();

			morphChildren(oldList, newList, {
				entryClass: skipEntryAnimation ? undefined : 'cart-drawer__item--entering',
				replaceContent: true,
			});

			// Swap the server image with the cached optimistic image (already decoded, no flash)
			if (cachedImg && this.#pendingMoveToTop) {
				const serverItem = oldList.querySelector(`[data-key^="${this.#pendingMoveToTop}:"]`);
				const serverImg = serverItem?.querySelector('img[data-cart-child="image"]');
				if (serverImg) serverImg.replaceWith(cachedImg);
			}

			// Reconcile footer (subtotal, checkout link). The optimistic preview
			// for the first item added to an empty cart builds the items list but
			// no footer, and morph reaches this path (both lists exist) instead of
			// the full-swap branch — so the footer must be CREATED here, not just
			// updated, or it stays absent (item visible, no subtotal/checkout).
			const oldFooter = contentEl.querySelector('.cart-drawer__footer');
			const newFooter = newContentEl.querySelector('.cart-drawer__footer');
			if (oldFooter && newFooter) {
				oldFooter.innerHTML = newFooter.innerHTML;
			} else if (newFooter && !oldFooter) {
				contentEl.appendChild(newFooter.cloneNode(true));
			} else if (oldFooter && !newFooter) {
				oldFooter.remove();
			}
		} catch (error) {
			console.error('CartDrawer: morph failed, falling back to full replace', error);
			contentEl.innerHTML = newContentEl.innerHTML;
		}

		this.#applyPendingMoveToTop(contentEl);
		this.#updateScrollableState();
	}

	/**
	 * Move a recently-added item to the top of the list after morph.
	 * Consumes #pendingMoveToTop so it only fires once.
	 * @param {HTMLElement} contentEl
	 */
	#applyPendingMoveToTop(contentEl) {
		const variantId = this.#pendingMoveToTop;
		if (!variantId) return;
		this.#pendingMoveToTop = null;

		const list = contentEl.querySelector('.cart-drawer__items');
		if (!list) return;

		const item = list.querySelector(`[data-key^="${variantId}:"]`);
		if (item && item !== list.firstElementChild) {
			list.prepend(item);
		}
	}

}

if (!customElements.get('cart-drawer')) {
	customElements.define('cart-drawer', CartDrawer);
}
